import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, ArrowRight, MapPin, Zap, TrendingUp, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import { fetchContent } from '@/lib/contentApi';

const CARD_STYLES = [
  { color: 'from-blue-500/10 to-indigo-500/10', span: 'md:col-span-2 lg:col-span-8' },
  { color: 'from-emerald-500/10 to-teal-500/10', span: 'md:col-span-2 lg:col-span-4' },
  { color: 'from-orange-500/10 to-amber-500/10', span: 'md:col-span-2 lg:col-span-6' },
  { color: 'from-pink-500/10 to-rose-500/10', span: 'md:col-span-2 lg:col-span-6' },
  { color: 'from-cyan-500/10 to-sky-500/10', span: 'md:col-span-2 lg:col-span-7' },
  { color: 'from-purple-500/10 to-violet-500/10', span: 'md:col-span-2 lg:col-span-5' },
];

const fallbackCompanies = [
  { name: 'Nexintell Limited', role: 'Founder & Director', url: 'https://www.nexintell.co',
    description: 'Nexintell Limited is a UK-based digital infrastructure intelligence company focused on making complex infrastructure systems legible, governable, and decision-ready.',
    detail: 'The company builds intelligence-led platforms that transform fragmented infrastructure data into structured insight for governments, enterprises, and technology ecosystems.' },
  { name: 'Infralytics-GIIS', role: 'Creator', url: 'https://www.infralytics.co',
    description: 'Infralytics-GIIS (Global Infrastructure Intelligence System) is a platform designed to deliver geospatial intelligence and system-level analytics for digital infrastructure planning, resilience, and sustainable development.',
    detail: 'It enables decision-makers to understand where infrastructure works, where it fails, and where investment delivers real outcomes rather than duplicated assets.' },
  { name: 'Evolve Digital Africa', role: 'Founder & Lead Engineer', url: 'https://www.evolvedigitalafrica.com.ng',
    description: 'Evolve Digital Africa is an ICT and telecommunications infrastructure company delivering end-to-end solutions across fibre-optic networks, microwave systems, enterprise connectivity, security infrastructure, and renewable energy systems.',
    detail: 'A flagship technical contribution from this work is Fibre X, a scalable FTTH network architecture designed by Ogaba Joseph Attah to enable rapid, cost-efficient last-mile broadband deployment across urban and underserved communities.' },
  { name: 'Grit & Style', role: 'Co-Founder', url: 'https://www.grit.ng',
    description: 'Grit & Style, also known as Future Inspired Clothing, is an innovative fashion tech brand based in Abuja, Nigeria, blending modern fashion with cutting-edge AI technology to create stylish, functional clothing for individuals of all ages.',
    detail: 'Empowering individuals—youth, adults, men, women, and children—by democratizing fashion design through AI-Powered Creation that turns simple text prompts into professional fashion sketches and prototypes.' },
  { name: 'iEvolve CIC', role: 'Founder', url: 'https://www.ievolve.uk',
    description: 'iEvolve CIC is a Community Interest Company dedicated to digital empowerment and social inclusion across Stockton-on-Tees, the North East, and the United Kingdom.',
    detail: 'From digital literacy to employment pathways, providing end-to-end support for communities and organisations navigating the digital landscape.' },
  { name: 'Subterradynamics', role: 'Head, Renewable & Green Energy | Co-Head, System & Technology', url: 'https://www.subterradynamics.com',
    description: 'Subterradynamics focuses on underground infrastructure intelligence, renewable energy systems, and mapping solutions for urban development and utility management.',
    detail: 'Leveraging advanced technologies to create comprehensive subsurface data systems that support smart city planning, green energy infrastructure, and optimization.' },
];

export default function CompaniesPage() {
  const [companies, setCompanies] = useState(fallbackCompanies);

  useEffect(() => {
    fetchContent('companies').then((items) => { if (items && items.length) setCompanies(items); });
  }, []);

  return (
    <>
      <SEOHead
        title="Companies Founded by Ogaba Joseph Attah"
        description="Explore the companies founded by Ogaba Joseph Attah: Nexintell Limited, Infralytics-GIIS, Evolve Digital Africa, and Subterradynamics - leaders in digital infrastructure intelligence."
        canonicalUrl="https://www.ogabajosephattah.com/companies"
      />
      
      <Navigation />
      
      <main className="pt-24 lg:pt-32">
        {/* Hero */}
        <section className="py-16 md:py-24" data-testid="companies-hero">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl"
            >
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-6">
                Ventures
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[0.95] mb-6" data-testid="companies-title">
                Companies Founded by Ogaba Joseph Attah
              </h1>
              <p className="text-lg text-muted-foreground">
                Building intelligence-led platforms and infrastructure solutions across digital connectivity, data systems, and emerging market technology.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Companies Grid - Bento Style */}
        <section className="py-12 md:py-16" data-testid="companies-grid">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
              {companies.map((company, index) => {
                const style = CARD_STYLES[index % CARD_STYLES.length];
                return (
                <motion.div
                  key={company.id || company.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`${style.span} group`}
                >
                  <a
                    href={company.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block h-full p-8 rounded-2xl border border-border bg-gradient-to-br ${style.color} hover:border-primary/50 hover:-translate-y-1 hover:shadow-xl transition-all duration-300`}
                    data-testid={`company-${company.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <div className="flex justify-between items-start mb-6">
                      <p className="text-xs uppercase tracking-wider text-primary font-bold">
                        {company.role}
                      </p>
                      <ExternalLink className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-4 group-hover:text-primary transition-colors">
                      {company.name}
                    </h2>
                    <p className="text-muted-foreground mb-4">
                      {company.description}
                    </p>
                    <p className="text-sm text-muted-foreground/80">
                      {company.detail}
                    </p>
                  </a>
                </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Fibre X Highlight */}
        <section className="py-24 md:py-32 bg-muted/30" data-testid="fibre-x-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">
                  Flagship Innovation
                </p>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
                  Fibre X Architecture
                </h2>
                <p className="text-muted-foreground mb-6">
                  Fibre X is a scalable FTTH network architecture designed by Ogaba Joseph Attah to enable rapid, cost-efficient last-mile broadband deployment across urban and underserved communities.
                </p>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary mt-2" />
                    <span>Modular expansion capabilities</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary mt-2" />
                    <span>Optimised fibre utilisation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary mt-2" />
                    <span>Future-ready growth without service disruption</span>
                  </li>
                </ul>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="relative"
              >
                <div className="aspect-video rounded-2xl overflow-hidden bg-muted">
                  <img
                    src="https://customer-assets.emergentagent.com/job_682de1e5-c93e-434b-afcf-b22d5a4ba52c/artifacts/acqvwdfd_IMG-20241020-WA0049.jpg"
                    alt="Ogaba Joseph Attah - Fibre X Innovation"
                    className="w-full h-full object-cover"
                    data-testid="fibre-x-image"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Adoptic Flagship */}
        <section className="py-24 md:py-32" data-testid="adoptic-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl mb-12"
            >
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">
                Nexintell Flagship Product
              </p>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6" data-testid="adoptic-title">
                Adoptic
              </h2>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-3">
                A Business Digital Inclusion Intelligence platform — a <span className="text-foreground font-medium">Digital Adoption Map</span>.
              </p>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                Adoptic measures business digital adoption alongside connectivity infrastructure and deprivation at the postcode level, in real time, for every business in a given geography.
              </p>
            </motion.div>

            <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-stretch mb-12">
              {/* Value Prop */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="lg:col-span-7 p-8 md:p-10 rounded-2xl bg-gradient-to-br from-primary/5 to-indigo-500/10 border border-primary/20"
                data-testid="adoptic-value-card"
              >
                <h3 className="text-xl md:text-2xl font-semibold mb-5">What Adoptic tells decision-makers</h3>
                <p className="text-muted-foreground mb-6">
                  It tells Local Authorities and Business Support Organisations not just what infrastructure exists, but <span className="text-foreground font-medium">whom it serves, whom it fails, exactly where to intervene, and what the economic return on that intervention will be</span>.
                </p>
                <ul className="space-y-4 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Postcode-level, real-time visibility for every business in a geography</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <TrendingUp className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Tracks connectivity investment and where it translates into economic activity</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Target className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Pinpoints intervention sites and quantifies economic return on action</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Zap className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span>Continuously rerunnable — no six-month consultancy waits</span>
                  </li>
                </ul>
              </motion.div>

              {/* Differentiators */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="lg:col-span-5 flex flex-col gap-4"
                data-testid="adoptic-comparison"
              >
                <div className="p-6 rounded-2xl bg-card border border-border hover:border-primary/40 transition-colors">
                  <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-2">Unlike the Ofcom Dashboard</p>
                  <p className="text-sm text-muted-foreground">Tells you where the cable is — but cannot tell you whether a single business on that street is using it, benefiting from it, or being left behind by it.</p>
                </div>
                <div className="p-6 rounded-2xl bg-card border border-border hover:border-primary/40 transition-colors">
                  <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-2">Unlike ESRI / IDOX</p>
                  <p className="text-sm text-muted-foreground">Manages infrastructure beautifully, but cannot model human impact or economic consequences.</p>
                </div>
                <div className="p-6 rounded-2xl bg-card border border-border hover:border-primary/40 transition-colors">
                  <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-2">Unlike Consultancy Reports</p>
                  <p className="text-sm text-muted-foreground">No six months and £50,000 producing a report outdated before it is printed. Adoptic can be rerun continuously.</p>
                </div>
              </motion.div>
            </div>

            {/* Deployment + CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="rounded-2xl border border-border bg-card overflow-hidden"
              data-testid="adoptic-deployment"
            >
              <div className="grid md:grid-cols-5">
                <div className="md:col-span-3 p-8 md:p-10">
                  <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-3">Live Deployment</p>
                  <h3 className="text-2xl md:text-3xl font-bold tracking-tight mb-4">Stockton Digital Register</h3>
                  <p className="text-muted-foreground mb-6">
                    Adoptic is currently deployed as the Stockton Digital Register — a live, postcode-level Business Digital Adoption Map for Stockton-on-Tees, tracking connectivity infrastructure investment against real economic activity in the community it serves.
                  </p>
                  <a
                    href="https://adoptic.io/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity"
                    data-testid="adoptic-stockton-cta"
                  >
                    Explore Stockton Digital Register
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
                <div className="md:col-span-2 bg-gradient-to-br from-primary/10 via-indigo-500/10 to-emerald-500/10 p-8 md:p-10 flex flex-col justify-center border-t md:border-t-0 md:border-l border-border">
                  <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-3">By the Numbers</p>
                  <dl className="space-y-4">
                    <div>
                      <dt className="text-xs text-muted-foreground">Granularity</dt>
                      <dd className="text-xl font-bold">Postcode-level</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">Coverage</dt>
                      <dd className="text-xl font-bold">Every business · per geography</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">Refresh</dt>
                      <dd className="text-xl font-bold">Real time · rerunnable</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 md:py-32" data-testid="companies-cta">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
                Interested in Collaboration?
              </h2>
              <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
                Explore partnership opportunities or learn more about working with these ventures.
              </p>
              <Button asChild size="lg" data-testid="companies-contact-cta">
                <a href="/contact">
                  Get in Touch
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
